import { InfoBox } from '@srcDS/components/organisms/map/InfoBox';
import styles from './Map.module.scss';
import { MAP_SETTINGS, USER_MARKER } from './utils';
import debounce from 'lodash.debounce';
import React, { useEffect, useRef, useState } from 'react';
import { GoogleMap, InfoWindow, Marker, useJsApiLoader } from '@react-google-maps/api';

import app from '@frontastic/catwalk/src/js/app/app';

interface Position {
  lat: number;
  lng: number;
}

interface Origin {
  storeId: number;
  storeName: string;
  address1: string;
  state: string;
  city: string;
  postcode: string;
  coordinates: Position;
  key: string;
}

interface MapContainerProps {
  origins: Origin[];
  originId: number | null;
  userPosition: Position | null;
  chosenOrigin: Origin | null;
  markerIcon: string | null;
  mapStyles: google.maps.MapTypeStyle[];
  customZoom: number | null;
  getStoresByBounds: (map: google.maps.Map | null) => void;
}

const containerStyle = {
  width: '100%',
  height: '100%',
};

const MapContainer: React.FC<MapContainerProps> = (props) => {
  const {
    origins = [],
    originId = null,
    userPosition = null,
    chosenOrigin = null,
    markerIcon = null,
    mapStyles = [],
    customZoom = null,
    getStoresByBounds = () => {},
  } = props;

  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [selectedOriginId, setSelectedOriginId] = useState<number | null>(originId);
  const selectedOrigin = origins?.find(({ storeId }) => selectedOriginId === storeId);

  const { MARKER_SIZE, DEFAULT_ZOOM, DEFAULT_CENTER, DEFAULT_MAP_OPTIONS, MARKER_USER_SIZE, PIXEL_OFFSET } =
    MAP_SETTINGS;

  const store = app.getStore();
  const googleKey = store.getState().app.context?.project?.configuration?.googleMap?.key;

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: googleKey,
  });

  const [map, setMap] = React.useState(null);

  const onLoad = React.useCallback(function callback(loadedMap) {
    if (!window.google?.maps) {
      console.error('Google Maps API not loaded');
      return;
    }

    const bounds = new window.google.maps.LatLngBounds(chosenOrigin?.coordinates || userPosition || DEFAULT_CENTER);
    loadedMap.fitBounds(bounds);
    setMap(loadedMap);
  }, []);

  const onUnmount = React.useCallback(function callback(map) {
    setMap(null);
  }, []);

  useEffect(() => {
    try {
      if (customZoom || !window.google?.maps) return;

      if (origins?.length) {
        const bounds = new window.google.maps.LatLngBounds();

        origins.forEach(({ coordinates: { lat, lng } }) => {
          bounds.extend(new window.google.maps.LatLng(lat, lng));
        });

        const northEast = bounds.getNorthEast();
        const southWest = bounds.getSouthWest();

        if (northEast && southWest && map) {
          if (northEast.equals(southWest)) {
            const position = new window.google.maps.LatLng(northEast.lat(), northEast.lng());
            map.panTo(position);
          } else {
            map.fitBounds(bounds);
          }
        }
      }
    } catch (error) {
      console.error('Error with store locator map:', error);
    }
  }, [origins, customZoom, map]);

  useEffect(() => {
    try {
      if (!chosenOrigin && userPosition && map && window.google?.maps) {
        const position = new window.google.maps.LatLng(userPosition.lat, userPosition.lng);
        map.panTo(position);
      }
    } catch (error) {
      console.error('Помилка при оновленні позиції користувача:', error);
    }
  }, [userPosition, chosenOrigin, map]);

  useEffect(() => {
    if (originId) {
      setSelectedOriginId(originId);
    }
  }, [originId]);

  useEffect(() => {
    if (customZoom && map && isLoaded) {
      setTimeout(() => {
        map?.setZoom(customZoom);
      }, 100);
    }
  }, [customZoom, map, isLoaded]);

  return (
    isLoaded && (
      <div className={styles.wrapper}>
        <GoogleMap
          onLoad={onLoad}
          options={{ ...DEFAULT_MAP_OPTIONS, styles: mapStyles }}
          zoom={customZoom || DEFAULT_ZOOM}
          mapContainerStyle={containerStyle}
          center={chosenOrigin?.coordinates || userPosition || DEFAULT_CENTER}
          onUnmount={onUnmount}
          onZoomChanged={debounce(() => getStoresByBounds(mapRef.current), 500)}
          onBoundsChanged={debounce(() => getStoresByBounds(mapRef.current), 500)}
        >
          {(chosenOrigin ? [chosenOrigin] : origins).map(({ coordinates: { lat, lng }, storeId }) => (
            <Marker
              ref={markerRef}
              key={storeId || `${lat}-${lng}`}
              position={{ lat, lng }}
              onClick={() => setSelectedOriginId(storeId)}
              icon={{
                scaledSize:
                  window.google && window.google.maps
                    ? new window.google.maps.Size(MARKER_SIZE, MARKER_SIZE)
                    : undefined,
                url: markerIcon,
              }}
            />
          ))}
          {userPosition && (
            <Marker
              ref={markerRef}
              position={{ lat: userPosition.lat, lng: userPosition.lng }}
              icon={{
                scaledSize:
                  window.google && window.google.maps
                    ? new window.google.maps.Size(MARKER_USER_SIZE, MARKER_USER_SIZE)
                    : undefined,
                url: USER_MARKER,
              }}
            />
          )}
          {selectedOrigin && (
            <InfoWindow
              onCloseClick={() => setSelectedOriginId(null)}
              position={{
                lat: selectedOrigin.coordinates.lat,
                lng: selectedOrigin.coordinates.lng,
              }}
              options={{
                pixelOffset:
                  window.google && window.google.maps
                    ? new window.google.maps.Size(PIXEL_OFFSET.MARKER.X, PIXEL_OFFSET.MARKER.Y)
                    : undefined,
              }}
            >
              <InfoBox
                onClose={() => setSelectedOriginId(null)}
                keyId={selectedOrigin.key}
                {...selectedOrigin}
              />
            </InfoWindow>
          )}
        </GoogleMap>
      </div>
    )
  );
};

export default MapContainer;
